import {Stack, TextInput} from '@mantine/core';
import React from 'react';

import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {Optional} from 'util/Types';

const MAX_NAME_LENGTH = 255;

export interface CreatePartFormProps {
  // State values (controlled)
  name: string;
  folderId: Optional<string>;

  // Change handlers
  onNameChange: (name: string) => void;
  onFolderIdChange: (folderId: string) => void;
}

export function getCreateFormValidation({name, folderId}: {name: string; folderId: Optional<string>}): {
  isValid: boolean;
  nameIsTooLong: boolean;
  nameErrorMessage?: string;
} {
  const nameOrFolderMissing = !folderId || name.trim().length === 0;
  const nameIsTooLong = name.length > MAX_NAME_LENGTH;
  const nameErrorMessage = nameIsTooLong
    ? `Names can be at most 255 characters long (currently ${name.length})`
    : undefined;

  return {
    isValid: !nameOrFolderMissing && !nameIsTooLong,
    nameIsTooLong,
    nameErrorMessage,
  };
}

export default function CreatePartForm({
  name,
  folderId,
  onNameChange,
  onFolderIdChange,
}: CreatePartFormProps): JSX.Element {
  const {nameErrorMessage} = getCreateFormValidation({name, folderId});

  return (
    <Stack spacing="md">
      <FormGroup>
        <FieldLabel label="Part name" isRequired={true} htmlFor="name" />
        <TextInput
          id="name"
          autoFocus={true}
          placeholder="Name"
          onChange={(e) => {
            onNameChange(e.target.value);
          }}
          value={name}
          error={nameErrorMessage}
          onFocus={(e) => {
            const input = e.target;
            input.setSelectionRange(0, input.value.length);
          }}
          aria-label="Part name"
        />
      </FormGroup>

      <FormGroup>
        <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
        <FolderSelector
          buttonSize={ButtonSize.MEDIUM}
          enableRedesign={true}
          showInventory={true}
          value={folderId}
          onChange={(e) => {
            onFolderIdChange(e.target.value);
          }}
          unsetOnFetchFail={true}
          allowNone={false}
        />
      </FormGroup>
    </Stack>
  );
}
